/**
 * A porta ÚNICA de leitura e gravação de tela.
 *
 * **Um formato só.** Não há detecção de formato, não há migração, não há
 * heurística escolhendo como interpretar o `TEMPLATEJSON` — o que existia antes
 * era justamente isso, e produziu três formatos coexistindo numa coluna só
 * (`BuilderJsonOutput`, `WebhookConfiguration`, `TemplateComponent[]`), escolhidos
 * por adivinhação no `mapToJson()`.
 *
 * Isso só é possível porque **nada em produção foi construído com o motor
 * antigo**. Sem tela viva no formato velho, não há o que preservar: o
 * `ScreenDocument` é o formato, ponto. Uma linha em formato antigo é recusada com
 * mensagem clara, e não convertida às escondidas.
 *
 * O que continua valendo da seção 1.5 do plano:
 *
 *  - `schemaVersion` no documento, para a PRÓXIMA mudança de formato ser
 *    versionada em vez de adivinhada;
 *  - `VERSION` da linha lido e devolvido, e conferido antes de gravar. Ver o
 *    comentario de `save()`: o broker deste projeto nao tem gravacao condicional,
 *    entao o que existe e uma checagem, nao um lock — e isso esta dito la por
 *    extenso, em vez de a promessa ficar no cabecalho.
 */
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { HTTPService } from '../../services/http.service';
import { UserAuthenticationServices } from '../../services/user-authentication.services';
import { CURRENT_SCHEMA_VERSION, ScreenDocument } from '../model/screen-schema';
import { buildIndex } from '../model/screen-index';
import { LIKE_ESCAPE, sqlInteger, sqlLike } from './sql-literal';

/** O que a leitura devolve. */
export interface LoadedScreen {
  readonly document: ScreenDocument;
  /** Chave numerica da linha. Ausente enquanto a tela nunca foi gravada. */
  readonly idTemplate?: number;
  /** `VERSION` da linha, base da checagem de concorrencia. */
  readonly rowVersion: number;
}

/**
 * Uma tela na listagem.
 *
 * De proposito NAO traz o `TEMPLATEJSON`: a listagem precisa de nome, data e
 * contagem, e trazer o documento inteiro de cada linha faria uma lista de 50
 * telas puxar alguns megabytes para exibir 50 titulos.
 */
export interface ScreenSummary {
  readonly idTemplate: number;
  readonly name: string;
  readonly description: string;
  readonly category: string;
  readonly fieldCount: number;
  readonly updatedAt: string;
  /**
   * A tela e da empresa de quem esta olhando.
   *
   * A listagem tambem traz as publicas, de outras empresas — abrir uma delas e
   * legitimo, excluir nao. Isto e o que o seletor usa para nao oferecer o botao.
   *
   * **Nao e permissao**: e o front que decide o que mostrar, e o broker aceitaria
   * a exclusao do mesmo jeito se alguem chamasse na mao. A guarda de verdade
   * pertence ao servidor, e esta anotada como pendencia de backend.
   */
  readonly isOwn: boolean;
}

/** O que a gravacao devolve. */
export interface SavedScreen {
  readonly idTemplate: number;
  readonly rowVersion: number;
}

/**
 * Alguem gravou a mesma tela enquanto voce editava.
 *
 * E um resultado esperado, nao uma falha: o editor mostra a escolha (recarregar
 * ou sobrescrever) em vez de um erro tecnico.
 */
export class ScreenConflictError extends Error {
  constructor(
    readonly expectedVersion: number,
    readonly actualVersion: number
  ) {
    super('A tela foi alterada por outra pessoa.');
    this.name = 'ScreenConflictError';
  }
}

/** A linha da listagem. */
interface ListRow {
  IDTEMPLATE?: number;
  CODECOMPANY?: number;
  NAME?: string;
  DESCRIPTION?: string;
  CATEGORY?: string;
  COMPONENTCOUNT?: number;
  MODIFIEDON?: string;
  CREATEDON?: string;
}

/** A linha crua, só com as colunas que interessam ao motor. */
interface TemplateRow {
  IDTEMPLATE?: number;
  TEMPLATEKEY?: string;
  NAME?: string;
  DESCRIPTION?: string;
  CATEGORY?: string;
  TEMPLATEJSON?: string;
  VERSION?: number;
}

@Injectable({ providedIn: 'root' })
export class ScreenDocumentApiService {

  private readonly uas = inject(UserAuthenticationServices);

  constructor(private readonly http: HTTPService) { }

  load(idTemplate: number): Observable<LoadedScreen> {
    const request = {
      entityName: 'Mktpscreentemplate',
      sql: 'SELECT IDTEMPLATE, TEMPLATEKEY, NAME, DESCRIPTION, CATEGORY, TEMPLATEJSON, VERSION'
        + ' FROM MKTPSCREENTEMPLATE'
        + ' WHERE IDTEMPLATE = ' + sqlInteger(idTemplate)
        + ' AND ISDELETED = 0'
    };

    return this.http.post(request, 'JsonBrokerServices/findnative', false, true, true).pipe(
      map((response: unknown) => this.toLoadedScreen(response, idTemplate)),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Interpreta um `TEMPLATEJSON` já em mãos. Síncrono: não há I/O.
   *
   * Serve aos testes e a quem já tem o texto — o editor da Fase 4, por exemplo,
   * ao pré-visualizar o que vai gravar.
   */
  parse(templateJson: string): ScreenDocument {
    let raw: unknown;

    try {
      raw = JSON.parse(templateJson);
    } catch {
      throw new Error('O conteúdo da tela não é um JSON válido.');
    }

    return this.asScreenDocument(raw);
  }

  /**
   * Confere que o JSON é um `ScreenDocument`, e recusa qualquer outra coisa.
   *
   * Recusar em voz alta é a decisão importante aqui. A alternativa — tentar
   * adivinhar e converter — é exatamente o que criou os três formatos que a
   * reescrita está eliminando.
   */
  private asScreenDocument(raw: unknown): ScreenDocument {
    if (raw === null || typeof raw !== 'object') {
      throw new Error('O conteúdo da tela está vazio ou não é um objeto.');
    }

    const candidate = raw as Partial<ScreenDocument>;

    if (candidate.root === undefined || candidate.root === null) {
      throw new Error(
        'Esta tela está num formato antigo, que não é mais suportado. '
        + 'Monte-a de novo no construtor.'
      );
    }

    if (typeof candidate.schemaVersion !== 'number') {
      throw new Error('A tela não declara a versão do formato.');
    }

    if (candidate.schemaVersion > CURRENT_SCHEMA_VERSION) {
      // Tela gravada por uma versão mais nova da aplicação. Abrir e gravar por
      // cima descartaria em silêncio o que esta versão não entende.
      throw new Error(
        'Esta tela foi gravada por uma versão mais nova do sistema '
        + '(formato ' + candidate.schemaVersion + '). Atualize a aplicação.'
      );
    }

    return candidate as ScreenDocument;
  }

  /**
   * As telas gravadas que este usuario pode abrir.
   *
   * `ISDELETED = 0` porque a exclusao e logica. O filtro por empresa segue a
   * mesma regra do resto do projeto: a propria empresa, mais as publicas.
   */
  list(search?: string): Observable<readonly ScreenSummary[]> {
    let sqlCondition = 'ISDELETED = 0 AND ISACTIVE = 1'
      + ' AND (CODECOMPANY = ' + sqlInteger(this.companyOfUser())
      + ' OR ISPUBLIC = 1)';

    const needle = (search ?? '').trim();

    if (needle.length > 0) {
      // Pelo ponto único (`sql-literal.ts`), e não por escape escrito aqui. O
      // `sqlLike` também neutraliza `%` e `_` do usuário: sem isso, digitar `%`
      // na busca casa com tudo, e a pessoa acha que a busca quebrou.
      const sqlAlvo = sqlLike(needle);

      sqlCondition = sqlCondition + ' AND (NAME LIKE ' + sqlAlvo + LIKE_ESCAPE
        + ' OR DESCRIPTION LIKE ' + sqlAlvo + LIKE_ESCAPE + ')';
    }

    const request = {
      entityName: 'Mktpscreentemplate',
      sql: 'SELECT IDTEMPLATE, NAME, DESCRIPTION, CATEGORY, COMPONENTCOUNT,'
        + ' MODIFIEDON, CREATEDON, CODECOMPANY'
        + ' FROM MKTPSCREENTEMPLATE'
        + ' WHERE ' + sqlCondition
        + ' ORDER BY COALESCE(MODIFIEDON, CREATEDON) DESC'
    };

    return this.http.post(request, 'JsonBrokerServices/findnative', false, true, true).pipe(
      map((response: unknown) => this.toSummaries(response)),
      catchError(error => throwError(() => error))
    );
  }

  private toSummaries(response: unknown): readonly ScreenSummary[] {
    const rows = this.readRows(response) as readonly ListRow[];
    const company = this.companyOfUser();
    const summaries: ScreenSummary[] = [];

    for (const row of rows) {
      if (typeof row.IDTEMPLATE !== 'number') {
        continue;
      }

      summaries.push({
        idTemplate: row.IDTEMPLATE,
        name: row.NAME ?? '',
        description: row.DESCRIPTION ?? '',
        category: row.CATEGORY ?? '',
        fieldCount: typeof row.COMPONENTCOUNT === 'number' ? row.COMPONENTCOUNT : 0,
        updatedAt: row.MODIFIEDON ?? row.CREATEDON ?? '',
        isOwn: Number(row.CODECOMPANY) === company
      });
    }

    return summaries;
  }

  /**
   * Exclui a tela. **Logicamente**: a linha continua no banco com `ISDELETED = 1`.
   *
   * Tres decisoes vale registrar:
   *
   * 1. **So as colunas da exclusao vao no pedido.** O `deleteTemplate` antigo
   *    mandava tambem `CODECOMPANY` e `CODECONTRACT` do usuario que estava
   *    excluindo — o que, numa tela publica de outra empresa, REESCREVIA o dono
   *    no ato de apagar. Nao copiei isso.
   *
   * 2. **Nao ha checagem de versao.** Ela existe no `save` porque duas pessoas
   *    editando produzem textos diferentes e um vence; aqui o resultado e o
   *    mesmo independentemente de quem chegou antes, e pedir a versao so
   *    acrescentaria uma ida ao banco que nao muda o desfecho.
   *
   * 3. **E idempotente.** Excluir duas vezes grava `ISDELETED = 1` duas vezes.
   *    Melhor do que estourar erro em cima de quem clicou no botao duas vezes.
   */
  remove(idTemplate: number): Observable<void> {
    const request = {
      entityName: 'Mktpscreentemplate',
      IDTEMPLATE: sqlInteger(idTemplate),
      ISDELETED: 1,
      DELETEDBY: this.uas.user.username,
      DELETEDON: new Date()
    };

    return this.http.post(request, 'JsonBrokerServices/save', true, true, true).pipe(
      map(() => undefined),
      catchError(error => throwError(() => error))
    );
  }

  // ---------------------------------------------------------------- gravacao

  /**
   * Grava a tela.
   *
   * **Sobre o travamento otimista, sem maquiar o que ele e.**
   *
   * O plano previa `UPDATE ... WHERE VERSION = :esperado`, que seria atomico: ou
   * a linha e minha, ou zero linhas mudam e eu sei que perdi. So que o broker
   * deste projeto tem apenas `JsonBrokerServices/save`, que grava por chave
   * primaria — **nao existe endpoint que aceite um WHERE**.
   *
   * Entao o que esta implementado aqui e uma checagem LE-ENTAO-GRAVA: leio a
   * `VERSION` atual, comparo com a que carreguei, e so gravo se baterem. Isso
   * **nao e um lock**: entre a leitura e a gravacao existe uma janela de
   * milissegundos em que outra pessoa pode gravar, e nesse caso a ultima
   * gravacao vence em silencio.
   *
   * Vale a pena mesmo assim, porque o caso real nao e a janela de
   * milissegundos: e duas pessoas com a mesma tela aberta por minutos. Esse
   * caso a checagem pega, e hoje ele passa despercebido — `updateTemplate()`
   * sobrescreve in-place sem olhar `VERSION` nenhuma.
   *
   * Para ser atomico de verdade, o backend precisa expor uma gravacao
   * condicional. Fica registrado como pedido, nao como pendencia deste arquivo.
   */
  save(
    document: ScreenDocument,
    idTemplate: number | undefined,
    expectedVersion: number
  ): Observable<SavedScreen> {

    if (idTemplate === undefined || idTemplate <= 0) {
      return this.insert(document);
    }

    return this.readVersion(idTemplate).pipe(
      switchMap(actual => {
        if (actual !== expectedVersion) {
          return throwError(() => new ScreenConflictError(expectedVersion, actual));
        }
        return this.update(document, idTemplate, expectedVersion);
      })
    );
  }

  private readVersion(idTemplate: number): Observable<number> {
    const request = {
      entityName: 'Mktpscreentemplate',
      sql: 'SELECT VERSION FROM MKTPSCREENTEMPLATE'
        + ' WHERE IDTEMPLATE = ' + sqlInteger(idTemplate)
    };

    return this.http.post(request, 'JsonBrokerServices/findnative', false, true, true).pipe(
      map((response: unknown) => {
        const rows = this.readRows(response);
        if (rows.length === 0) {
          // A tela sumiu do banco. Gravar por cima recriaria uma linha que
          // alguem apagou de proposito.
          throw new Error('Esta tela foi removida por outra pessoa.');
        }
        return typeof rows[0].VERSION === 'number' ? rows[0].VERSION : 1;
      })
    );
  }

  private insert(document: ScreenDocument): Observable<SavedScreen> {
    const request = {
      ...this.columnsOf(document, 1),
      entityName: 'Mktpscreentemplate',
      TEMPLATEKEY: this.buildKey(document.meta.name),
      CODECOMPANY: this.uas.user.codecompany,
      CODECONTRACT: this.contractOfUser(),
      ISPUBLIC: 0,
      ISACTIVE: 1,
      ISPUBLISHED: 0,
      IDUSER: this.uas.user.iduser,
      CREATEDBY: this.uas.user.username
    };

    return this.http.post(request, 'JsonBrokerServices/save', true, true, true).pipe(
      map((response: unknown) => {
        const created = (response as { IDTEMPLATE?: number } | null)?.IDTEMPLATE;

        if (typeof created !== 'number') {
          throw new Error('A tela nao pode ser criada.');
        }

        return { idTemplate: created, rowVersion: 1 };
      }),
      catchError(error => throwError(() => error))
    );
  }

  private update(
    document: ScreenDocument,
    idTemplate: number,
    expectedVersion: number
  ): Observable<SavedScreen> {

    const nextVersion = expectedVersion + 1;

    const request = {
      ...this.columnsOf(document, nextVersion),
      entityName: 'Mktpscreentemplate',
      IDTEMPLATE: idTemplate,
      MODIFIEDBY: this.uas.user.username
    };

    return this.http.post(request, 'JsonBrokerServices/save', true, true, true).pipe(
      map(() => ({ idTemplate, rowVersion: nextVersion })),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * As colunas que descrevem a tela.
   *
   * `COMPONENTCOUNT` e denormalizado e existia antes; mantido preenchido porque
   * o seletor de modelos o exibe. Conta o que GRAVA valor, nao todo no da
   * arvore: dizer "47 componentes" contando painel e linha seria enganoso.
   */
  private columnsOf(document: ScreenDocument, version: number): Record<string, unknown> {
    const index = buildIndex(document);

    let fieldCount = 0;
    for (const node of index.inOrder) {
      if (node.binding !== undefined) {
        fieldCount = fieldCount + 1;
      }
    }

    return {
      NAME: document.meta.name,
      DESCRIPTION: document.meta.description ?? '',
      CATEGORY: document.meta.category ?? 'custom',
      TEMPLATEJSON: JSON.stringify(document),
      COMPONENTCOUNT: fieldCount,
      VERSION: version
    };
  }

  /**
   * O contrato do usuario, repassado COMO ESTA.
   *
   * O modelo guarda `codecontract` como texto. Converter para numero aqui
   * poderia comer zero a esquerda, e o valor so serve para ir para a coluna —
   * quem tipa a coluna e o banco, nao este arquivo.
   */
  private contractOfUser(): string | number | null {
    const list = this.uas.user.companylist;

    if (list !== undefined && list !== null && list.length > 0) {
      return list[0].codecontract;
    }

    return null;
  }

  /** Chave textual, no mesmo formato que o resto do projeto ja usa. */
  private buildKey(name: string): string {
    const normalized = (name ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    return (normalized.length > 0 ? normalized : 'tela') + '-' + Date.now();
  }

  private toLoadedScreen(response: unknown, idTemplate: number): LoadedScreen {
    const rows = this.readRows(response);

    if (rows.length === 0) {
      throw new Error('Tela ' + idTemplate + ' não encontrada');
    }

    const row = rows[0];
    const document = this.parse(row.TEMPLATEJSON ?? '');

    return {
      document: {
        ...document,
        id: row.TEMPLATEKEY ?? String(idTemplate),
        meta: {
          ...document.meta,
          name: row.NAME ?? document.meta.name,
          description: row.DESCRIPTION ?? document.meta.description,
          category: row.CATEGORY ?? document.meta.category
        }
      },
      idTemplate: typeof row.IDTEMPLATE === 'number' ? row.IDTEMPLATE : idTemplate,
      rowVersion: typeof row.VERSION === 'number' ? row.VERSION : 1
    };
  }

  private readRows(response: unknown): readonly (TemplateRow & ListRow)[] {
    if (response === null || typeof response !== 'object') {
      return [];
    }

    const sql = (response as { SQL?: unknown }).SQL;
    if (!Array.isArray(sql)) {
      return [];
    }

    return sql as (TemplateRow & ListRow)[];
  }

  /**
   * O id entra na consulta por concatenação, como no resto do projeto — então
   * forçar inteiro aqui não é preciosismo: é o que impede que uma rota mal
   * formada vire injeção de SQL. A Fase 7 parametriza as consultas de verdade;
   * até lá, esta guarda é o mínimo.
   */
  /**
   * A empresa do usuario, como inteiro, para entrar na consulta.
   *
   * Metodo proprio em vez de `asInteger`: a mensagem de erro daquele fala de
   * "id de tela", e quem le um log precisa saber QUAL valor estava errado.
   */
  private companyOfUser(): number {
    const company = Math.trunc(Number(this.uas.user.codecompany));

    if (!Number.isFinite(company)) {
      throw new Error('Empresa do usuario invalida: '
        + String(this.uas.user.codecompany));
    }

    return company;
  }

  private asInteger(value: number): number {
    const rounded = Math.trunc(Number(value));

    if (!Number.isFinite(rounded)) {
      throw new Error('Id de tela inválido: ' + String(value));
    }

    return rounded;
  }
}
